'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.repeatUntilEventOnElement = exports.listAttrIncludes = exports.removeFromListAttr = exports.addToListAttr = exports.moveFocusToNext = exports.getPrevTabbableElementInclusive = exports.getNextTabbableElementExclusive = exports.getTabbableElements = exports.elementIsOnScreen = exports.closest = exports.matches = exports.prepend = exports.elementHasClass = undefined;

var _Selectors = require('../constants/Selectors');

var _underscore = require('./underscore');

var _Arrays = require('./Arrays');

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

// Polyfill for el.classList.contains(className) that doesn't fail for SVGs in IE11
var elementHasClass = exports.elementHasClass = function elementHasClass(el, className) {
  return !!(el && el.classList && el.classList.contains(className));
};

// Polyfill for node.prepend()
var prepend = exports.prepend = function prepend(parent, child) {
  var firstChild = parent.firstChild;
  if (firstChild) {
    parent.insertBefore(child, firstChild);
    return;
  }
  parent.appendChild(child);
};

// Polyfill for el.matches(selector)
var matches = exports.matches = function matches(el, selector) {
  if (!el) {
    return false;
  }
  if (el.matches) {
    return el.matches(selector);
  }
  if (el.msMatchesSelector) {
    return el.msMatchesSelector(selector);
  }
  if (el.parentElement) {
    var matchingSiblings = el.parentElement.querySelectorAll(selector);
    for (var i = 0; i < matchingSiblings.length; i++) {
      if (matchingSiblings[i] === el) {
        return true;
      }
    }
  }
  return false;
};

// Polyfill for el.closest(selector)
var closest = exports.closest = function closest(el, selector) {
  if (!el) {
    return null;
  }
  if (el.closest) {
    return el.closest(selector);
  }
  var currentEl = el;
  do {
    if (matches(currentEl, selector)) {
      return currentEl;
    }
    currentEl = currentEl.parentElement;
  } while (currentEl);
  return null;
};

// Is el within the bounds of the given screen?
var elementIsOnScreen = exports.elementIsOnScreen = function elementIsOnScreen(el) {
  var screen = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : window;

  var elementBounds = el.getBoundingClientRect();
  var xWithinBounds = elementBounds.right >= 0 && elementBounds.left <= screen.innerWidth;
  var yWithinBounds = elementBounds.bottom >= 0 && elementBounds.top <= screen.innerHeight;
  return xWithinBounds && yWithinBounds;
};

// Is el visible (for tabbing purposes)?
var elementIsVisible = function elementIsVisible(el) {
  return window.getComputedStyle(el, null).visibility !== 'hidden';
};

// Returns an array containing every tabbable element within the given container. The array order
// reflects the tabbing order, unless positive tabIndex values (e.g. tabIndex={7}) are used.
// Such tabIndex values are exceedingly rare in modern web apps, so don't worry about it.
var getTabbableElements = exports.getTabbableElements = function getTabbableElements(container) {
  return [].concat(_toConsumableArray(container.querySelectorAll(_Selectors.FOCUSABLE))).filter(function (el) {
    // Hidden elements aren't tabbable. Sadly, no CSS query can exclude such elements.
    return elementIsVisible(el);
  });
};

// Returns the next element after the given element (and its descendants) in the document tab order.
var getNextTabbableElementExclusive = exports.getNextTabbableElementExclusive = function getNextTabbableElementExclusive(startEl) {
  var allElements = [].concat(_toConsumableArray(document.querySelectorAll('*')));
  for (var i = allElements.indexOf(startEl) + 1; i < allElements.length; i++) {
    var el = allElements[i];
    if (startEl.contains(el)) {
      continue;
    }
    if (matches(el, _Selectors.FOCUSABLE) && elementIsVisible(el)) {
      return el;
    }
  }
  return null;
};

// Returns the previous element in the document tab order, *including* the given element and its
// descendants. This is equivalent to what the browser would focus if the user pressed Shift+Tab
// from the nextSibling of the given element, for example.
var getPrevTabbableElementInclusive = exports.getPrevTabbableElementInclusive = function getPrevTabbableElementInclusive(startEl) {
  var focusableDescendants = getTabbableElements(startEl);
  if (focusableDescendants.length) {
    return (0, _underscore.last)(focusableDescendants);
  }
  if (matches(startEl, _Selectors.FOCUSABLE)) {
    return startEl;
  }
  var allElements = [].concat(_toConsumableArray(document.querySelectorAll('*')));
  for (var i = allElements.indexOf(startEl) - 1; i >= 0; i--) {
    var el = allElements[i];
    if (matches(el, _Selectors.FOCUSABLE) && elementIsVisible(el)) {
      return el;
    }
  }
  return null;
};

/**
 * Goes through all the elements matching `selector` within `container` starting from the one that
 * currently has the focus (or from the start), then moves the focus into the next one that can be
 * focused (or, if `reverse = true`, the previous one).
 *
 * @param {string} selector
 * @param {HTMLElement} container
 * @param {boolean} reverse
 * @return {?HTMLElement} The element that received the focus
 */
var moveFocusToNext = exports.moveFocusToNext = function moveFocusToNext(selector, container) {
  var reverse = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var allMatches = [].concat(_toConsumableArray(container.querySelectorAll(selector)));
  if (allMatches.length === 0) return null;

  // Get an ordered list of matching elements don't already contain the focus
  var currentMenuItem = closest(document.activeElement, selector);
  var candidateMatches = (0, _Arrays.rotateAround)(allMatches, currentMenuItem);
  candidateMatches = candidateMatches.filter(function (menuItem) {
    return !menuItem.contains(document.activeElement);
  });

  // If reverse is set, flip the order so we move backward in the DOM
  if (reverse) candidateMatches.reverse();

  // Move the focus to the first focusable element found within a candidate element
  for (var i = 0; i < candidateMatches.length; i++) {
    var focusTarget = candidateMatches[i].querySelector(_Selectors.FOCUSABLE);
    if (focusTarget) {
      focusTarget.focus();
      return focusTarget;
    }
  }
  return null;
};

var getAttrAsList = function getAttrAsList(element, attrKey) {
  return element.hasAttribute(attrKey) ? element.getAttribute(attrKey).split(' ') : [];
};

/**
 * Add a single value to a DOM attribute that contains a list of space-separated values.
 *
 * @param {HTMLElement} element
 * @param {string} attrKey
 * @param {string} newValue
 */
var addToListAttr = exports.addToListAttr = function addToListAttr(element, attrKey, newValue) {
  if (!element) return;
  var attrAsArray = getAttrAsList(element, attrKey);
  if (attrAsArray.includes(newValue)) return;
  element.setAttribute(attrKey, attrAsArray.concat(newValue).join(' '));
};

/**
 * Remove a single value from a DOM attribute that contains a list of space-separated values.
 *
 * @param {HTMLElement} element
 * @param {string} attrKey
 * @param {string} value
 */
var removeFromListAttr = exports.removeFromListAttr = function removeFromListAttr(element, attrKey, value) {
  if (!element) return;
  var attrAsArray = getAttrAsList(element, attrKey);
  if (!attrAsArray.includes(value)) return;
  attrAsArray.splice(attrAsArray.indexOf(value), 1);
  element.setAttribute(attrKey, attrAsArray.join(' '));
};

/**
 * @param {?string} attr
 * @param {string} value
 * @returns {boolean} True if the space-separated list attr includes the given value
 */
var listAttrIncludes = exports.listAttrIncludes = function listAttrIncludes(attr, value) {
  return (attr || '').split(' ').includes(value);
};

// Invoke the `callback` every `frequency` ms until `element` fires one of the `events`.
var repeatUntilEventOnElement = exports.repeatUntilEventOnElement = function repeatUntilEventOnElement(callback, frequency, element, events) {
  var handle = {
    active: true,
    count: 0
  };

  var timer = setInterval(function () {
    callback();
    handle.count += 1;
  }, frequency);

  var stop = handle.stop = function () {
    handle.active = false;
    clearInterval(timer);
    events.forEach(function (event) {
      element.removeEventListener(event, stop);
    });
  };

  events.forEach(function (event) {
    element.addEventListener(event, stop);
  });

  return handle;
};